import { useState, useEffect, useCallback } from "react";
import { SessionsService } from "@/services/SessionsService";
import type { Session, SessionTimeJumpAnalysis } from "@/services/SessionsService";

/**
 * Sessions longer than this are implausible for kitesurfing and are the signature of
 * a mid-session device-clock jump (SRF-2710). Matches the threshold in the ticket.
 */
const SUSPICIOUS_DURATION_SECONDS = 10 * 60 * 60;

interface SessionTimeJumpBannerProps {
  session: Session;
  /** Called after a successful repair so the parent can reload the session. */
  onRepaired: () => void;
}

const formatEpoch = (epoch: number): string =>
  new Date(epoch).toLocaleString("en-GB", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
};

const formatOffset = (offsetMs: number): string => {
  const hours = offsetMs / 3_600_000;
  return hours >= 24 ? `${(hours / 24).toFixed(1)} days` : `${hours.toFixed(2)} hours`;
};

/**
 * Shown on the GPS Track tab when a session's duration looks impossible. Detects a
 * mid-session clock jump, previews the correction, and lets support apply it.
 *
 * <p>Trimming the track cannot fix these sessions — the bad timestamps sit in the
 * middle, so cutting the edges would discard genuine riding data.
 */
export const SessionTimeJumpBanner: React.FC<SessionTimeJumpBannerProps> = ({ session, onRepaired }) => {
  const [analysis, setAnalysis] = useState<SessionTimeJumpAnalysis | null>(null);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [repaired, setRepaired] = useState(false);

  const isSuspicious = (session.timeonwater ?? 0) > SUSPICIOUS_DURATION_SECONDS;

  const analyse = useCallback(async () => {
    setIsAnalysing(true);
    setError(null);
    try {
      setAnalysis(await SessionsService.detectSessionTimeJump(session.ID));
    } catch (err) {
      console.error("Error detecting session time jump:", err);
      setError("Could not analyse the GPS track for time jumps.");
    } finally {
      setIsAnalysing(false);
    }
  }, [session.ID]);

  // Only probe the backend for sessions that actually look broken, so the common
  // case costs nothing.
  useEffect(() => {
    if (isSuspicious && !repaired) {
      analyse();
    }
  }, [isSuspicious, repaired, analyse]);

  const handleRepair = async () => {
    setIsRepairing(true);
    setError(null);
    try {
      await SessionsService.repairSessionTimeJump(session.ID);
      setRepaired(true);
      setShowConfirm(false);
      onRepaired();
    } catch (err) {
      console.error("Error repairing session time jump:", err);
      setError("Repair failed. The GPS track was not modified.");
    } finally {
      setIsRepairing(false);
    }
  };

  if (!isSuspicious && !repaired) return null;

  if (repaired) {
    return (
      <div className="mb-4 rounded-lg border border-green-300 bg-green-50 p-4">
        <p className="text-sm font-medium text-green-800">
          Time jump repaired — the session date, duration and jump times have been corrected.
        </p>
      </div>
    );
  }

  return (
    <div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
      <div className="flex items-start gap-3">
        <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>

        <div className="min-w-0 flex-1">
          <h4 className="text-sm font-semibold text-amber-900">
            Implausible duration ({formatDuration(session.timeonwater ?? 0)})
          </h4>

          {isAnalysing && (
            <p className="mt-1 text-sm text-amber-700">Analysing GPS track…</p>
          )}

          {error && <p className="mt-1 text-sm text-red-700">{error}</p>}

          {analysis && !isAnalysing && !analysis.hasJumps && (
            <p className="mt-1 text-sm text-amber-700">
              {/* Surface the backend's reason verbatim — it distinguishes "track is fine,
                  the duration has another cause" from "couldn't be analysed at all"
                  (e.g. a legacy track with no GPS storage path). */}
              {analysis.reason
                ? `${analysis.reason} — this needs manual review.`
                : "No mid-session time jump found — this long duration has another cause and needs manual review."}
            </p>
          )}

          {analysis && !isAnalysing && analysis.hasJumps && !analysis.repairable && (
            <p className="mt-1 text-sm text-amber-700">
              A time jump was detected but cannot be repaired automatically
              {analysis.reason ? `: ${analysis.reason}` : "."}
            </p>
          )}

          {analysis && !isAnalysing && analysis.repairable && (
            <>
              <p className="mt-1 text-sm text-amber-800">
                The watch clock jumped{" "}
                <strong>{formatOffset(analysis.jumps[0]?.offsetMs ?? 0)}</strong> forward mid-session
                {analysis.jumps.length > 1 ? ` (${analysis.jumps.length} jumps total)` : ""} —
                the recording started before GPS time was acquired. Trimming cannot fix this because the
                bad timestamps are in the middle of the track.
              </p>

              <dl className="mt-3 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                <div className="flex justify-between gap-2 sm:block">
                  <dt className="text-amber-700">Currently</dt>
                  <dd className="font-medium text-amber-900">
                    {formatEpoch(analysis.originalStartEpoch)} · {formatDuration(analysis.originalDurationSeconds)}
                  </dd>
                </div>
                <div className="flex justify-between gap-2 sm:block">
                  <dt className="text-amber-700">After repair</dt>
                  <dd className="font-medium text-green-800">
                    {formatEpoch(analysis.repairedStartEpoch)} · {formatDuration(analysis.repairedDurationSeconds)}
                  </dd>
                </div>
              </dl>

              <p className="mt-2 text-xs text-amber-600">
                {analysis.pointCount} GPS points kept — no data is discarded. The original track is backed up
                before the change.
              </p>

              {!showConfirm ? (
                <button
                  onClick={() => setShowConfirm(true)}
                  className="mt-3 rounded-md border border-amber-400 bg-white px-3 py-1.5 text-sm font-medium text-amber-800 transition-colors hover:bg-amber-100"
                >
                  Fix session time
                </button>
              ) : (
                <div className="mt-3 rounded-md border border-amber-300 bg-white p-3">
                  <p className="text-sm text-gray-700">
                    Rewrite the GPS track, session date/duration and {" "}
                    jump timestamps to the corrected time? This is recorded in the audit log.
                  </p>
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={handleRepair}
                      disabled={isRepairing}
                      className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                    >
                      {isRepairing ? "Repairing…" : "Confirm repair"}
                    </button>
                    <button
                      onClick={() => setShowConfirm(false)}
                      disabled={isRepairing}
                      className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionTimeJumpBanner;
